import {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    CourseItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    DateTimePicker,
    PageContainer,
    SelectBox,
    StateCard,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    StudentItem
} from "../students/type";
import type {
    TeacherItem
} from "../teachers/type";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    RefreshCcwIcon,
    BookOpenIcon,
    PencilIcon,
    TrashIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const Courses = () => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        courses,
        setCourses
    ] = useState<CourseItem[]>([]);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState<boolean>(false);

    const [
        students,
        setStudents
    ] = useState<StudentItem[]>([]);

    const [
        teachers,
        setTeachers
    ] = useState<TeacherItem[]>([]);

    const [
        modalVisible,
        setModalVisible
    ] = useState(false);

    const [
        editId,
        setEditId
    ] = useState<string | null>(null);

    const [
        formData,
        setFormData
    ] = useState({
        student_ids: [] as string[],
        teacher_ids: [] as string[],
        start_date: "",
        end_date: "",
        title: ""
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const [
        searchQuery,
        setSearchQuery
    ] = useState("");

    const [
        debouncedSearch,
        setDebouncedSearch
    ] = useState("");

    const [
        previewItem,
        setPreviewItem
    ] = useState<CourseItem | null>(null);

    const [
        pagination,
        setPagination
    ] = useState({
        totalRecords: 0,
        currentPage: 1,
        totalPages: 1,
        limit: 10
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPagination(prev => ({
                ...prev,
                currentPage: 1
            }));
        }, 500);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchStudents = useCallback(async () => {
        try {
            const token = await AsyncStorage.getItem("authToken");
            const response = await fetch(`${process.env.API_URL}/students?limit=1000`, {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setStudents(result.data);
            }
        } catch (error) {
            console.error("Öğrenciler alınamadı:", error);
        }
    }, [
        activeOrganizationId,
        user?.role
    ]);

    const fetchTeachers = useCallback(async () => {
        try {
            const token = await AsyncStorage.getItem("authToken");
            const response = await fetch(`${process.env.API_URL}/teachers?limit=1000`, {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setTeachers(result.data);
            }
        } catch (error) {
            console.error("Hocalar alınamadı:", error);
        }
    }, [
        activeOrganizationId,
        user?.role
    ]);

    const fetchCourses = useCallback(async (page: number = 1) => {
        try {
            setIsLoading(true);
            const token = await AsyncStorage.getItem("authToken");
            const url = new URL(`${process.env.API_URL}/courses`);
            url.searchParams.append("page", page.toString());
            url.searchParams.append("limit", pagination.limit.toString());

            if (debouncedSearch) {
                url.searchParams.append("search", debouncedSearch);
            }

            if (activeOrganizationId && user?.role === "root") {
                url.searchParams.append("organization_id", activeOrganizationId);
            }

            const response = await fetch(url.toString(), {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setCourses(result.data);
                if (result.meta) {
                    setPagination(result.meta);
                }
            }
        } catch (error) {
            console.error("Kurslar alınamadı:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        activeOrganizationId,
        debouncedSearch,
        user?.role,
        pagination.limit
    ]);

    useEffect(() => {
        fetchStudents();
        fetchTeachers();
    }, [
        fetchStudents,
        fetchTeachers
    ]);

    useEffect(() => {
        if (user?.role === "root" && !activeOrganizationId) {
            setCourses([]);
            setPagination({
                totalRecords: 0,
                currentPage: 1,
                totalPages: 1,
                limit: 10
            });
            return;
        }

        fetchCourses(pagination.currentPage);
    }, [
        activeOrganizationId,
        fetchCourses,
        pagination.currentPage,
        user?.role
    ]);

    const handleSave = async () => {
        if (!formData.title) {
            NCoreUIKitToast.open({
                title: localize("courses-toast-empty-title"),
                type: "danger"
            });
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = editId
                ? `${process.env.API_URL}/courses/${editId}`
                : `${process.env.API_URL}/courses`;

            const response = await fetch(url, {
                body: JSON.stringify({
                    ...formData,
                    ...(activeOrganizationId && user?.role === "root" ? {
                        organization_id: activeOrganizationId
                    } : {})
                }),
                headers: {
                    "Content-Type": "application/json",
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                },
                method: editId ? "PUT" : "POST"
            });

            const result = await response.json();
            if (result.status === "success") {
                setModalVisible(false);
                setEditId(null);
                setFormData({
                    student_ids: [],
                    teacher_ids: [],
                    start_date: "",
                    end_date: "",
                    title: ""
                });
                fetchCourses(1);
            } else {
                NCoreUIKitToast.open({
                    title: result.message || localize("courses-toast-unknown-error"),
                    type: "danger"
                });
            }
        } catch (error) {
            console.error("Kayıt hatası:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (id: string, name: string) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const response = await fetch(`${process.env.API_URL}/courses/${id}`, {
                            headers: {
                                "Content-Type": "application/json",
                                ...(activeOrganizationId && user?.role === "root" ? {
                                    "x-organization-id": activeOrganizationId
                                } : {}),
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });

                        const result = await response.json();
                        if (result.status === "success") {
                            fetchCourses(pagination.currentPage);
                        } else {
                            NCoreUIKitToast.open({
                                title: result.message || localize("courses-toast-delete-failed"),
                                type: "danger"
                            });
                        }
                    } catch (error) {
                        console.error("Silme hatası:", error);
                    } finally {
                        isDeleting = false;

                        closeAnimation();
                    }
                },
                title: localize("courses-dialog-delete-primary"),
                type: "danger"
            },
            content: localize("courses-dialog-delete-content").replace("%{name}", name),
            secondaryButtonProps: {
                title: localize("courses-dialog-delete-secondary"),
                onPress: ({
                    closeAnimation
                }) => closeAnimation(),
                type: "neutral"
            },
            title: localize("courses-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: newPage
            }));
        }
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerRow,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("courses-page-title")}
                </Text>
                <View
                    style={{
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchCourses(pagination.currentPage);
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("courses-page-add-button")}
                        onPress={() => {
                            setEditId(null);
                            setFormData({
                                student_ids: [],
                                teacher_ids: [],
                                start_date: "",
                                end_date: "",
                                title: ""
                            });
                            setModalVisible(true);
                        }}
                        type="primary"
                    />
                </View>
            </View>
            <TextInput
                placeholder={localize("courses-page-search-placeholder")}
                title={localize("courses-page-search-placeholder")}
                style={{
                    marginBottom: spaces.spacingXl
                }}
                onChangeText={setSearchQuery}
                spreadBehaviour="stretch"
                value={searchQuery}
            />
            {
                isLoading && courses.length === 0 ?
                    <ActivityIndicator
                        color={colors.content.icon.default}
                        size="large"
                    />
                :
                    courses.length === 0 ? (
                        <StateCard
                            subTitle={localize("courses-empty-subtitle")}
                            title={localize("courses-empty-title")}
                        />) : (
                            <ScrollView
                                contentContainerStyle={[
                                    stylesheet.listContent,
                                    {
                                        paddingBottom: spaces.spacingXl,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                                showsVerticalScrollIndicator={false}
                            >
                                {courses.map((item) => <TouchableOpacity
                                    style={[
                                        stylesheet.itemContainer,
                                        {
                                            backgroundColor: colors.content.container.mid,
                                            alignItems: isMobile ? "stretch" : "center",
                                            flexDirection: isMobile ? "column" : "row",
                                            borderRadius: radiuses.md,
                                            padding: spaces.spacingMd,
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                    onPress={() => setPreviewItem(item)}
                                    activeOpacity={0.7}
                                    key={item.id}
                                >
                                    <View
                                        style={[
                                            stylesheet.itemContent,
                                            {
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <View
                                            style={[
                                                stylesheet.itemIcon,
                                                {
                                                    backgroundColor: colors.content.container.default,
                                                    borderRadius: radiuses.full
                                                }
                                            ]}
                                        >
                                            <BookOpenIcon
                                                color={colors.content.icon.emphasized}
                                                size={24}
                                            />
                                        </View>
                                        <View
                                            style={[
                                                stylesheet.itemInfo,
                                                {
                                                    gap: spaces.spacingSm
                                                }
                                            ]}
                                        >
                                            <Text
                                                variant="titleMediumSize"
                                            >
                                                {item.title}
                                            </Text>
                                            {(item.start_date || item.end_date) && (
                                                <Text
                                                    variant="bodyMediumSize"
                                                    color="mid"
                                                >
                                                    {item.start_date ? new Date(item.start_date).toLocaleDateString("tr-TR") : "?"} - {item.end_date ? new Date(item.end_date).toLocaleDateString("tr-TR") : "?"}
                                                </Text>)}
                                            {item.student_ids && item.student_ids.length > 0 && (
                                                <Text
                                                    variant="bodySmallSize"
                                                    color="mid"
                                                >
                                                    {localize("courses-preview-students")} {item.student_ids.map(sId => students.find(s => s.id === sId)?.name_surname).filter(Boolean).join(", ")}
                                                </Text>)}
                                            {item.teacher_ids && item.teacher_ids.length > 0 && (
                                                <Text
                                                    variant="bodySmallSize"
                                                    color="mid"
                                                >
                                                    {localize("courses-preview-teachers")} {item.teacher_ids.map(tId => teachers.find(t => t.id === tId)?.name_surname).filter(Boolean).join(", ")}
                                                </Text>)}
                                        </View>
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.itemActions,
                                            {
                                                flexDirection: isMobile ? "column" : "row",
                                                alignItems: "stretch",
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => <PencilIcon
                                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                size={size}
                                            />
                                            }
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            onPress={() => {
                                                setEditId(item.id);
                                                setFormData({
                                                    student_ids: item.student_ids || [],
                                                    teacher_ids: item.teacher_ids || [],
                                                    start_date: item.start_date || "",
                                                    end_date: item.end_date || "",
                                                    title: item.title || ""
                                                });
                                                setModalVisible(true);
                                            }}
                                            title={localize("roles-list-edit-button")}
                                            type="neutral"
                                        />
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => <TrashIcon
                                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                size={size}
                                            />
                                            }
                                            onPress={() => handleDelete(item.id, item.title)}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            title={localize("courses-list-delete-button")}
                                            type="danger"
                                        />
                                    </View>
                                </TouchableOpacity>)}
                            </ScrollView>)
            }
            <Modal
                onOverlayPress={({
                    closeAnimation
                }) => closeAnimation(() => setPreviewItem(null))}
                onClosed={() => setPreviewItem(null)}
                portalName="modal-system"
                id="course-preview-modal"
                isActive={!!previewItem}
            >
                <View
                    style={[
                        stylesheet.modalContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: isMobile ? "100%" : 500,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXl
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {localize("courses-preview-title")}
                    </Text>
                    <ScrollView
                        showsVerticalScrollIndicator={false}
                        style={{
                            flexShrink: 1
                        }}
                    >
                        <View
                            style={[
                                stylesheet.formContainer,
                                {
                                    marginTop: spaces.spacingMd,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text>{localize("courses-preview-course-name").replace("%{title}", previewItem?.title || "")}</Text>
                            <Text>{localize("courses-preview-start-date").replace("%{date}", previewItem?.start_date ? new Date(previewItem.start_date).toLocaleDateString("tr-TR") : "-")}</Text>
                            <Text>{localize("courses-preview-end-date").replace("%{date}", previewItem?.end_date ? new Date(previewItem.end_date).toLocaleDateString("tr-TR") : "-")}</Text>
                            <View
                                style={[
                                    stylesheet.previewSection,
                                    {
                                        marginTop: spaces.spacingMd,
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                >
                                    {localize("courses-preview-teachers")}
                                </Text>
                                {
                                    (!previewItem?.teacher_ids || previewItem.teacher_ids.length === 0) && (
                                        <Text
                                            color="low"
                                        >
                                            {localize("courses-preview-no-teachers")}
                                        </Text>)
                                }
                                {
                                    previewItem?.teacher_ids && previewItem.teacher_ids.length > 0 && previewItem.teacher_ids.map(id => {
                                        const teacher = teachers.find(t => t.id === id);
                                        return <Text
                                            key={`teacher-${id}`}
                                        >
                                            • {teacher ? teacher.name_surname : id}
                                        </Text>;
                                    })
                                }
                            </View>
                            <View
                                style={[
                                    stylesheet.previewSection,
                                    {
                                        marginTop: spaces.spacingMd,
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                >
                                    {localize("courses-preview-students")}
                                </Text>
                                {
                                    (!previewItem?.student_ids || previewItem.student_ids.length === 0) && (
                                        <Text
                                            color="low"
                                        >
                                            {localize("courses-preview-no-students")}
                                        </Text>)
                                }
                                {
                                    previewItem?.student_ids && previewItem.student_ids.length > 0 && previewItem.student_ids.map(id => {
                                        const student = students.find(s => s.id === id);
                                        return <Text
                                            key={`student-${id}`}
                                        >
                                            • {student ? student.name_surname : id}
                                        </Text>;
                                    })
                                }
                            </View>
                        </View>
                    </ScrollView>
                    <View
                        style={[
                            stylesheet.modalActions,
                            {
                                flexDirection: isMobile ? "column-reverse" : "row",
                                marginTop: spaces.spacingXl,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={localize("courses-preview-close")}
                            onPress={() => setPreviewItem(null)}
                            spreadBehaviour="stretch"
                            type="neutral"
                        />
                    </View>
                </View>
            </Modal>
            {pagination.totalPages > 1 && (
                <View
                    style={[
                        stylesheet.paginationContainer,
                        {
                            marginBottom: spaces.spacingXl,
                            marginTop: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        onPress={() => handlePageChange(pagination.currentPage - 1)}
                        title={localize("courses-pagination-prev")}
                        isDisabled={pagination.currentPage === 1}
                        icon={ChevronLeftIcon}
                        color="secondary"
                    />
                    <Text
                        variant="bodyMediumSize"
                    >
                        {localize("courses-pagination-info")
                            .replace("%{currentPage}", pagination.currentPage.toString())
                            .replace("%{totalPages}", pagination.totalPages.toString())}
                    </Text>
                    <Button
                        isDisabled={pagination.currentPage === pagination.totalPages}
                        onPress={() => handlePageChange(pagination.currentPage + 1)}
                        title={localize("courses-pagination-next")}
                        icon={ChevronRightIcon}
                        iconDirection="right"
                        color="secondary"
                    />
                </View>)}
        </View>
        <Modal
            onOverlayPress={({
                closeAnimation
            }) => closeAnimation(() => {
                setModalVisible(false);
                setEditId(null);
            })}
            onClosed={() => {
                setModalVisible(false);
                setEditId(null);
            }}
            portalName="modal-system"
            isActive={modalVisible}
            id="course-modal"
        >
            <View
                style={[
                    stylesheet.modalContent,
                    {
                        backgroundColor: colors.content.container.default,
                        width: isMobile ? "100%" : 500,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {editId ? localize("courses-modal-edit-title") : localize("courses-modal-add-title")}
                </Text>
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    style={{
                        flexShrink: 1
                    }}
                >
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                marginTop: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <TextInput
                            key={`course-title-${modalVisible ? "visible" : "hidden"}`}
                            placeholder={localize("courses-modal-name-placeholder")}
                            title={localize("courses-modal-name-placeholder")}
                            onChangeText={(text) => setFormData(prev => ({
                                ...prev,
                                title: text
                            }))}
                            defaultValue={formData.title}
                            spreadBehaviour="stretch"
                        />
                        <View
                            style={[
                                stylesheet.formRow,
                                {
                                    flexDirection: isMobile ? "column" : "row",
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.formColumnLeft,
                                    {
                                        paddingRight: isMobile ? 0 : spaces.spacingSm,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <DateTimePicker
                                    initialDate={formData.start_date ? new Date(formData.start_date) : undefined}
                                    onChange={({
                                        date
                                    }) => setFormData(prev => ({
                                        ...prev,
                                        start_date: date ? new Date(date).toISOString() : ""
                                    }))}
                                    title={localize("courses-modal-start-date")}
                                    dateTimeSheetProps={{
                                        portalName: "modal-system"
                                    }}
                                    spreadBehaviour="stretch"
                                />
                            </View>
                            <View
                                style={[
                                    stylesheet.formColumnRight,
                                    {
                                        paddingLeft: isMobile ? 0 : spaces.spacingSm,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <DateTimePicker
                                    initialDate={formData.end_date ? new Date(formData.end_date) : undefined}
                                    onChange={({
                                        date
                                    }) => setFormData(prev => ({
                                        ...prev,
                                        end_date: date ? new Date(date).toISOString() : ""
                                    }))}
                                    title={localize("courses-modal-end-date")}
                                    dateTimeSheetProps={{
                                        portalName: "modal-system"
                                    }}
                                    spreadBehaviour="stretch"
                                />
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.inputWrapper,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <SelectBox
                                key={`selectbox-students-${modalVisible ? "visible" : "hidden"}`}
                                placeholder={localize("courses-modal-students-placeholder")}
                                initialSelectedItems={students.map(s => ({
                                    __title: s.name_surname,
                                    __key: s.id
                                })).filter(s => formData.student_ids.includes(s.__key))}
                                title={localize("courses-modal-students-label")}
                                onChange={(selected) => setFormData(prev => ({
                                    ...prev,
                                    student_ids: selected.map(s => s.__key)
                                }))}
                                titleExtractor={(item) => item.__title}
                                keyExtractor={(item) => item.__key}
                                selectSheetProps={{
                                    portalName: "modal-system"
                                }}
                                data={students.map(s => ({
                                    __title: s.name_surname,
                                    __key: s.id
                                }))}
                                spreadBehaviour="stretch"
                                isMultipleSelect={true}
                            />
                        </View>
                        <View
                            style={[
                                stylesheet.inputWrapper,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <SelectBox
                                key={`selectbox-teachers-${modalVisible ? "visible" : "hidden"}`}
                                placeholder={localize("courses-modal-teachers-placeholder")}
                                initialSelectedItems={teachers.map(t => ({
                                    __title: t.name_surname,
                                    __key: t.id
                                })).filter(t => formData.teacher_ids.includes(t.__key))}
                                title={localize("courses-modal-teachers-label")}
                                onChange={(selected) => setFormData(prev => ({
                                    ...prev,
                                    teacher_ids: selected.map(t => t.__key)
                                }))}
                                titleExtractor={(item) => item.__title}
                                keyExtractor={(item) => item.__key}
                                selectSheetProps={{
                                    portalName: "modal-system"
                                }}
                                data={teachers.map(t => ({
                                    __title: t.name_surname,
                                    __key: t.id
                                }))}
                                spreadBehaviour="stretch"
                                isMultipleSelect={true}
                            />
                        </View>
                    </View>
                </ScrollView>
                <View
                    style={[
                        stylesheet.modalActions,
                        {
                            flexDirection: isMobile ? "column-reverse" : "row",
                            marginTop: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        title={localize("courses-dialog-delete-secondary")}
                        onPress={() => setModalVisible(false)}
                        spreadBehaviour="stretch"
                        type="neutral"
                    />
                    <Button
                        title={localize("courses-modal-save")}
                        spreadBehaviour="stretch"
                        isLoading={isSubmitting}
                        onPress={handleSave}
                        type="primary"
                    />
                </View>
            </View>
        </Modal>
    </PageContainer>;
};

export default Courses;
